// Shared PDF capture + share/download for the KYC report cards (Pan, Gstin,
// Cin, CinLookup, Social, Equifax — Admin and User variants). Reuses the
// same navigator.share-with-files-falling-back-to-download pattern already
// established in src/pages/market-intelligence/shareExport.js, so behaviour
// is consistent across the app.
//
// IMPORTANT — this captures whatever DOM node the caller's ref points to.
// Each report component (Admin or User) has its own ref pointing at its own
// card. There is no shared state between them, so "Share" on the Admin
// component can only ever produce the Admin PDF, and "Share" on the User
// component can only ever produce the User PDF — the distinction is
// structural, not a flag that could be left in the wrong position.

async function elementToPdfBlob(element, { bgColor = '#fffbf5' } = {}) {
  const html2canvas = (await import('html2canvas')).default;
  const { default: jsPDF } = await import('jspdf');
  const { drawWatermark, drawCTAButtons, CTA_BLOCK_HEIGHT } = await import('../pages/market-intelligence/shareExport');

  const canvas = await html2canvas(element, {
    backgroundColor: bgColor,
    scale: 2,
    useCORS: true,
    // .no-print covers the Share/Download button row itself, and any
    // in-app-only chrome (e.g. collapse chevrons) that shouldn't appear
    // in the exported document.
    ignoreElements: (el) => el.classList?.contains('no-print'),
  });

  const pageW = 595; // A4 @ 72dpi, points
  const pageH = 842;
  const ratio = pageW / canvas.width;
  const pageHeightPx = pageH / ratio;

  const doc = new jsPDF({ unit: 'pt', format: [pageW, pageH] });

  let renderedPx = 0;
  let pageIndex = 0;
  let lastImgHeightPt = 0;
  while (renderedPx < canvas.height) {
    if (pageIndex > 0) doc.addPage();
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx);

    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceHeightPx;
    const sliceCtx = slice.getContext('2d');
    // Fill with the report's own background first. Without this, any
    // sub-pixel rounding gap at a page boundary is left transparent —
    // and JPEG (no alpha channel) renders transparent as solid black,
    // which is what showed up as a hard black line cutting across
    // content at every page break.
    sliceCtx.fillStyle = bgColor;
    sliceCtx.fillRect(0, 0, slice.width, slice.height);
    sliceCtx.drawImage(
      canvas, 0, renderedPx, canvas.width, sliceHeightPx,
      0, 0, canvas.width, sliceHeightPx
    );

    doc.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageW, sliceHeightPx * ratio);
    lastImgHeightPt = sliceHeightPx * ratio;
    renderedPx += sliceHeightPx;
    pageIndex += 1;
  }

  const marginX = 36;
  const contentW = pageW - marginX * 2;
  let ctaY = lastImgHeightPt + 20;
  if (ctaY > pageH - CTA_BLOCK_HEIGHT - 20) {
    doc.addPage();
    drawWatermark(doc, pageW, pageH);
    ctaY = 40;
  }
  drawCTAButtons(doc, marginX, contentW, ctaY);

  return doc.output('blob');
}

async function shareBlobOrDownload(blob, fileName, title, text) {
  const file = new File([blob], fileName, { type: 'application/pdf' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return 'shared';
    } catch (e) {
      // user cancelled the share sheet — fall through to a direct download
      // so the action doesn't silently do nothing
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
  return 'downloaded';
}

// Used by the Share button — tries the native share sheet with the real
// PDF file attached, falls back to a plain download if sharing isn't
// available or the person cancels.
export async function shareReportAsPdf(elementRef, fileName, title, text, bgColor) {
  if (!elementRef?.current) return;
  const blob = await elementToPdfBlob(elementRef.current, { bgColor });
  return shareBlobOrDownload(blob, fileName, title, text);
}

// Used by the Download button — always saves directly, no share sheet.
export async function downloadReportAsPdf(elementRef, fileName, bgColor) {
  if (!elementRef?.current) return;
  const blob = await elementToPdfBlob(elementRef.current, { bgColor });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
